<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Draggable from 'vuedraggable';

import KanbanCard from '../components/KanbanCard.vue';
import AddCardModal from '../components/AddCardModal.vue';
import ManageBoardModal from '../components/ManageBoardModal.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';

const store = useStore();
const { t } = useI18n();

const boards = useMapGetter('kanban/getBoards');
const stages = useMapGetter('kanban/getStages');
const items = useMapGetter('kanban/getItems');
const uiFlags = useMapGetter('kanban/getUIFlags');

const selectedBoardId = ref(null);
const addCardModalRef = ref(null);
const manageModalRef = ref(null);
const addCardStageId = ref(null);

const isFetching = computed(() => uiFlags.value.isFetching);

const boardOptions = computed(() =>
  boards.value.map(board => ({ value: board.id, label: board.name }))
);

// Local, mutable copy of items grouped by stage for draggable.
const columns = ref({});

const buildColumns = () => {
  const grouped = {};
  stages.value.forEach(stage => {
    grouped[stage.id] = items.value
      .filter(item => item.kanban_stage_id === stage.id)
      .sort((a, b) => a.position - b.position);
  });
  columns.value = grouped;
};

watch([stages, items], buildColumns, { deep: true });

const loadBoardData = boardId => {
  if (!boardId) return;
  store.dispatch('kanban/getStages', boardId);
  store.dispatch('kanban/getItems', boardId);
};

const onBoardChange = boardId => {
  selectedBoardId.value = boardId;
  loadBoardData(boardId);
};

// Drag-and-drop persistence: optimistic update, revert + toast on error.
const onDrop = async (stageId, event) => {
  // vuedraggable fires `change` with added/moved descriptors.
  const change = event.added || event.moved;
  if (!change) return;

  const movedItem = change.element;
  const newPosition = change.newIndex;

  // Snapshot for revert
  const snapshot = JSON.parse(JSON.stringify(items.value));

  try {
    await store.dispatch('kanban/moveItem', {
      boardId: selectedBoardId.value,
      id: movedItem.id,
      kanbanStageId: stageId,
      position: newPosition,
    });
  } catch (error) {
    // Revert local state to server truth
    store.dispatch('kanban/setItems', snapshot);
    buildColumns();
    useAlert(t('KANBAN.API.MOVE_ERROR'));
  }
};

const openAddCard = (stageId = null) => {
  addCardStageId.value = stageId || stages.value[0]?.id || null;
  addCardModalRef.value?.open();
};

const onCardAdded = () => {
  loadBoardData(selectedBoardId.value);
};

const removeCard = async item => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('KANBAN.CARD.REMOVE_CONFIRM'))) return;
  try {
    await store.dispatch('kanban/deleteItem', {
      boardId: selectedBoardId.value,
      id: item.id,
    });
    useAlert(t('KANBAN.API.REMOVE_SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN.API.REMOVE_ERROR'));
  }
};

const openManage = () => manageModalRef.value?.open();

const onManageUpdated = () => {
  store.dispatch('kanban/getBoards');
  if (selectedBoardId.value) loadBoardData(selectedBoardId.value);
};

const onManageBoardSelected = boardId => {
  if (boardId) onBoardChange(boardId);
};

onMounted(async () => {
  await store.dispatch('kanban/getBoards');
  if (boards.value.length) {
    onBoardChange(boards.value[0].id);
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="flex items-center justify-between gap-3 px-6 py-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3">
        <h1 class="text-lg font-medium text-n-slate-12">
          {{ t('KANBAN.HEADER_TITLE') }}
        </h1>
        <div v-if="boards.length" class="w-56">
          <ComboBox
            :model-value="selectedBoardId"
            :options="boardOptions"
            :placeholder="t('KANBAN.SELECT_BOARD')"
            @update:model-value="onBoardChange"
          />
        </div>
      </div>
      <div class="flex items-center gap-2">
        <NextButton
          v-if="selectedBoardId && stages.length"
          sm
          blue
          icon="i-lucide-plus"
          :label="t('KANBAN.ADD_CARD')"
          @click="openAddCard()"
        />
        <NextButton
          sm
          slate
          faded
          icon="i-lucide-settings"
          :label="t('KANBAN.MANAGE_BOARD')"
          @click="openManage"
        />
      </div>
    </header>

    <div class="flex-1 overflow-auto">
      <div v-if="isFetching && !stages.length" class="flex justify-center p-6">
        <Spinner />
      </div>
      <EmptyStateLayout
        v-else-if="!boards.length"
        :title="t('KANBAN.EMPTY_STATE.TITLE')"
        :subtitle="t('KANBAN.EMPTY_STATE.SUBTITLE')"
      >
        <template #actions>
          <NextButton
            blue
            icon="i-lucide-plus"
            :label="t('KANBAN.MANAGE_BOARD')"
            @click="openManage"
          />
        </template>
      </EmptyStateLayout>
      <p
        v-else-if="!selectedBoardId"
        class="p-6 text-sm text-center text-n-slate-11"
      >
        {{ t('KANBAN.NO_BOARD_SELECTED') }}
      </p>
      <div v-else class="flex h-full gap-4 p-4 overflow-x-auto">
        <div
          v-for="stage in stages"
          :key="stage.id"
          class="flex flex-col w-72 overflow-hidden border rounded-lg shadow-sm shrink-0 border-n-weak bg-n-background"
        >
          <!-- Accent bar: the stage colour is what tells the columns apart, so
          it needs a surface of its own. A dot alone is invisible on the light
          theme, where the lane and the page are both near-white. -->
          <div
            class="h-1 shrink-0"
            :style="{ backgroundColor: stage.color || '#1f93ff' }"
          />
          <div
            class="flex items-center justify-between gap-2 px-3 py-2 border-b bg-n-solid-1 border-n-weak"
          >
            <div class="flex items-center gap-2 min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ stage.name }}
              </span>
              <span
                class="px-1.5 py-0.5 text-xs rounded-full shrink-0 bg-n-alpha-2 text-n-slate-11"
              >
                {{ (columns[stage.id] || []).length }}
              </span>
            </div>
            <button
              type="button"
              class="p-1 rounded text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1"
              :aria-label="t('KANBAN.ADD_CARD')"
              @click="openAddCard(stage.id)"
            >
              <span class="i-lucide-plus size-4" />
            </button>
          </div>
          <Draggable
            :list="columns[stage.id]"
            group="kanban-cards"
            item-key="id"
            animation="150"
            ghost-class="opacity-40"
            class="flex flex-col flex-1 gap-2 p-2 overflow-y-auto min-h-12"
            @change="onDrop(stage.id, $event)"
          >
            <template #item="{ element }">
              <KanbanCard :item="element" @remove="removeCard" />
            </template>
            <template #footer>
              <p
                v-if="!(columns[stage.id] || []).length"
                class="p-2 text-xs text-center text-n-slate-10"
              >
                {{ t('KANBAN.STAGE.EMPTY') }}
              </p>
            </template>
          </Draggable>
        </div>
      </div>
    </div>

    <AddCardModal
      v-if="selectedBoardId"
      ref="addCardModalRef"
      :board-id="selectedBoardId"
      :stage-id="addCardStageId"
      @added="onCardAdded"
    />
    <ManageBoardModal
      ref="manageModalRef"
      :selected-board-id="selectedBoardId"
      @updated="onManageUpdated"
      @board-selected="onManageBoardSelected"
    />
  </div>
</template>
